import { useState, useEffect } from 'react';
import api from '../api';
import { useLab } from '../i18n/LabContext';
import { useLang } from '../i18n/LanguageContext';

const STATUS_COLORS = {
  available: 'var(--green)',
  busy: 'var(--purple)',
  broken: 'var(--red)',
};

export default function Computers() {
  const { t } = useLang();
  const { labs, labId, currentLab, refreshLabs } = useLab();
  const STATUS_LABELS = {
    available: t('statusAvailable'),
    busy: t('statusBusy'),
    broken: t('statusBroken'),
  };
  const selectedLab = labId;
  const [computers, setComputers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ lab_id: '', number: '', type: 'pc', hourly_rate: '' });
  const [shutdownSaving, setShutdownSaving] = useState(false);
  const [depositInput, setDepositInput] = useState('');
  const [depositSaving, setDepositSaving] = useState(false);
  const [rateModal, setRateModal] = useState(null); // { id, number, hourly_rate }
  const [rateInput, setRateInput] = useState('');
  const [rateSaving, setRateSaving] = useState(false);
  const [specsModal, setSpecsModal] = useState(null);
  const [specsForm, setSpecsForm] = useState({ cpu: '', gpu: '', ram_gb: '', monitor_hz: '', monitor_inch: '', peripherals: '', photo_url: '' });
  const [specsSaving, setSpecsSaving] = useState(false);
  const [hwResetting, setHwResetting] = useState(false);

  useEffect(() => {
    if (labId) setForm(f => ({ ...f, lab_id: labId }));
  }, [labId]);

  const TYPE_META = {
    pc:          { icon: '🖥', label: 'Kompyuter (PC)' },
    billiard:    { icon: '🎱', label: 'Bilyard stoli' },
    playstation: { icon: '🎮', label: 'PlayStation' },
  };

  const openRateModal = (pc) => {
    setRateModal(pc);
    setRateInput(pc.hourly_rate ? String(Math.round(parseFloat(pc.hourly_rate))) : '');
  };

  const openSpecsModal = (pc) => {
    setSpecsModal(pc);
    setSpecsForm({
      cpu: pc.cpu || '',
      gpu: pc.gpu || '',
      ram_gb: pc.ram_gb || '',
      monitor_hz: pc.monitor_hz || '',
      monitor_inch: pc.monitor_inch || '',
      peripherals: pc.peripherals || '',
      photo_url: pc.photo_url || '',
    });
  };

  const saveSpecs = async () => {
    if (!specsModal) return;
    setSpecsSaving(true);
    try {
      const body = {};
      Object.entries(specsForm).forEach(([k, v]) => { if (v !== '') body[k] = v; });
      await api.put(`/maintenance/computer/${specsModal.id}/specs`, body);
      setComputers(prev => prev.map(c => c.id === specsModal.id ? { ...c, ...body } : c));
      setSpecsModal(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Xato');
    } finally {
      setSpecsSaving(false);
    }
  };

  const saveRate = async () => {
    if (!rateModal) return;
    setRateSaving(true);
    try {
      const val = rateInput.trim() === '' ? null : parseInt(rateInput);
      await api.patch(`/owner/computers/${rateModal.id}`, { hourly_rate: val });
      setComputers(prev => prev.map(c =>
        c.id === rateModal.id ? { ...c, hourly_rate: val } : c
      ));
      setRateModal(null);
    } catch (e) {
      alert(e.response?.data?.error || t('error'));
    } finally {
      setRateSaving(false);
    }
  };

  const resetHardware = async () => {
    if (!rateModal) return;
    if (!window.confirm(`PC-${String(rateModal.number).padStart(2, '0')} hardware ID ni tozalash? Keyingi ulanishda yangi qurilma avtomatik ro'yxatdan o'tadi.`)) return;
    setHwResetting(true);
    try {
      await api.patch(`/owner/computers/${rateModal.id}/reset-hardware`);
      alert('Hardware ID tozalandi. Endi dastur qayta ishga tushirilganda yangi qurilma qabul qilinadi.');
      setRateModal(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Xato');
    } finally {
      setHwResetting(false);
    }
  };

  const saveShutdownSettings = async (enabled, delay) => {
    if (!selectedLab) return;
    setShutdownSaving(true);
    try {
      const r = await api.patch(`/owner/labs/${selectedLab}/settings`, {
        auto_shutdown_enabled: enabled,
        auto_shutdown_delay: parseInt(delay) || 15,
      });
      refreshLabs();
    } catch (e) {
      alert(e.response?.data?.error || t('error'));
    } finally {
      setShutdownSaving(false);
    }
  };

  useEffect(() => {
    if (selectedLab) {
      api.get(`/labs/${selectedLab}`).then(r => setComputers(r.data.computers || []));
      const lab = labs.find(l => l.id == selectedLab);
      setDepositInput(lab?.booking_deposit != null ? String(lab.booking_deposit) : '0');
    }
  }, [selectedLab, labs]);

  const saveDeposit = async () => {
    if (!selectedLab) return;
    setDepositSaving(true);
    try {
      await api.patch(`/owner/labs/${selectedLab}/settings`, {
        booking_deposit: parseFloat(depositInput) || 0,
      });
      refreshLabs();
    } catch (e) {
      alert(e.response?.data?.error || t('error'));
    } finally {
      setDepositSaving(false);
    }
  };


  const addComputer = async (e) => {
    e.preventDefault();
    try {
      await api.post('/owner/computers', form);
      setShowAdd(false);
      api.get(`/labs/${selectedLab}`).then(r => setComputers(r.data.computers || []));
    } catch (err) {
      alert(err.response?.data?.error || t('error'));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>{t('computersPageTitle')}</h1>
          <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>{computers.length} {t('pcCount')}</div>
        </div>
        <button onClick={() => setShowAdd(true)} style={{
          background: 'linear-gradient(135deg, var(--purple), #5b21b6)', border: 'none',
          color: 'white', padding: '10px 20px', borderRadius: 10, fontSize: 13,
          fontWeight: 600, cursor: 'pointer'
        }}>{t('addPc')}</button>
      </div>

      {/* Soatbay narx bloki */}
      {computers.length > 0 && (
        <BulkRateBox computers={computers} selectedLab={selectedLab} onDone={
          () => api.get(`/labs/${selectedLab}`).then(r => setComputers(r.data.computers || []))
        } />
      )}

      {/* Grid */}
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
        ⬇ Qurilmaga bosing — soatbay narxini o'rnatish uchun
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 12, marginBottom: 24 }}>
        {computers.map(pc => {
          const tm = TYPE_META[pc.type] || TYPE_META.pc;
          const typeAccent = pc.type === 'billiard' ? '#10b981' : pc.type === 'playstation' ? '#8b5cf6' : null;
          const borderCol = typeAccent || STATUS_COLORS[pc.status] || 'var(--border)';
          return (
            <div key={pc.id} onClick={() => openRateModal(pc)} style={{
              background: typeAccent
                ? `${typeAccent}18`
                : `rgba(${pc.status === 'available' ? '0,255,136' : pc.status === 'busy' ? '124,58,237' : '255,59,92'}, 0.08)`,
              border: `1px solid ${borderCol}`,
              borderRadius: 12, padding: '12px 8px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
              userSelect: 'none',
            }}>
              <div style={{ fontSize: 18, lineHeight: 1, marginBottom: 2 }}>{tm.icon}</div>
              <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 18, fontWeight: 700, color: typeAccent || STATUS_COLORS[pc.status] }}>
                {String(pc.number).padStart(2, '0')}
              </div>
              <div style={{ fontSize: 9, color: typeAccent || STATUS_COLORS[pc.status], marginTop: 2 }}>
                {STATUS_LABELS[pc.status] || pc.status}
              </div>
              {pc.hourly_rate ? (
                <div style={{ fontSize: 9, color: 'var(--cyan)', marginTop: 4, fontWeight: 600 }}>
                  {Number(pc.hourly_rate).toLocaleString()}
                </div>
              ) : (
                <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 4 }}>—</div>
              )}
            </div>
          );
        })}
        {computers.length === 0 && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', color: 'var(--text3)', padding: 40 }}>
            {t('noPcs')}
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[key] }} />
            {label}
          </div>
        ))}
      </div>

      {/* Auto-shutdown sozlamasi */}
      {currentLab && (
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>⚡ {t('autoShutdown')}</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
            {t('autoShutdownDesc')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            {/* Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                onClick={() => saveShutdownSettings(!currentLab.auto_shutdown_enabled, currentLab.auto_shutdown_delay || 15)}
                style={{
                  width: 48, height: 26, borderRadius: 13, cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                  background: currentLab.auto_shutdown_enabled ? 'var(--green)' : 'var(--border)',
                }}
              >
                <div style={{
                  position: 'absolute', top: 3, width: 20, height: 20, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s', left: currentLab.auto_shutdown_enabled ? 25 : 3,
                }} />
              </div>
              <span style={{ fontSize: 13, color: currentLab.auto_shutdown_enabled ? 'var(--green)' : 'var(--text2)', fontWeight: 600 }}>
                {currentLab.auto_shutdown_enabled ? t('enabled') : t('disabled')}
              </span>
              {shutdownSaving && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{t('saving')}</span>}
            </div>

            {/* Delay */}
            {currentLab.auto_shutdown_enabled && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>{t('idleTime')}</span>
                {[5, 10, 15, 20, 30].map(m => (
                  <button key={m} onClick={() => saveShutdownSettings(true, m)} style={{
                    padding: '5px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
                    background: (currentLab.auto_shutdown_delay || 15) === m ? 'rgba(0,212,255,0.15)' : 'transparent',
                    border: `1px solid ${(currentLab.auto_shutdown_delay || 15) === m ? 'var(--cyan)' : 'var(--border)'}`,
                    color: (currentLab.auto_shutdown_delay || 15) === m ? 'var(--cyan)' : 'var(--text2)',
                  }}>{m} {t('min')}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bron zalogi sozlamasi */}
      {currentLab && (
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>💰 {t('bookingDeposit')}</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
            {t('bookingDepositDesc')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="number"
              min="0"
              step="1000"
              value={depositInput}
              onChange={e => setDepositInput(e.target.value)}
              placeholder="0"
              style={{
                width: 160, background: 'var(--card)', border: '1px solid var(--border)',
                color: 'var(--text)', padding: '8px 14px', borderRadius: 8, fontSize: 14, outline: 'none'
              }}
            />
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>{t('currency')}</span>
            <button onClick={saveDeposit} disabled={depositSaving} style={{
              background: 'linear-gradient(135deg, var(--cyan), #0369a1)', border: 'none',
              color: 'white', padding: '8px 20px', borderRadius: 8, fontSize: 13,
              fontWeight: 600, cursor: depositSaving ? 'not-allowed' : 'pointer', opacity: depositSaving ? 0.7 : 1
            }}>
              {depositSaving ? t('saving') : t('save')}
            </button>
            {currentLab.booking_deposit > 0 && (
              <span style={{ fontSize: 12, color: 'var(--green)' }}>
                {t('currentDeposit')} {Number(currentLab.booking_deposit).toLocaleString()} {t('currency')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Hourly Rate Modal */}
      {rateModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'var(--card)', border: '1px solid var(--border)',
            borderRadius: 16, padding: 32, width: 340
          }}>
            <h3 style={{ marginBottom: 6 }}>💻 PC-{String(rateModal.number).padStart(2, '0')} — Soatbay narx</h3>
            <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
              Foydalanuvchi paketsiz kirishda bu narx bo'yicha hisoblanadi. Bo'sh qoldirsangiz — paketsiz kirish yo'q.
            </p>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Narx (so'm/soat)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="number"
                  min="0"
                  step="500"
                  value={rateInput}
                  onChange={e => setRateInput(e.target.value)}
                  placeholder="masalan: 15000"
                  autoFocus
                  style={{
                    flex: 1, background: 'var(--card2)', border: '1px solid var(--border)',
                    color: 'var(--text)', padding: '10px 14px', borderRadius: 8, fontSize: 14, outline: 'none'
                  }}
                />
                <span style={{ fontSize: 13, color: 'var(--text2)', whiteSpace: 'nowrap' }}>so'm/soat</span>
              </div>
              {rateInput && !isNaN(parseInt(rateInput)) && (
                <div style={{ fontSize: 11, color: 'var(--cyan)', marginTop: 8 }}>
                  Misol: 75 000 so'm balansli foydalanuvchi → {Math.floor(75000 / parseInt(rateInput) * 60)} daqiqa = {(75000 / parseInt(rateInput)).toFixed(1)} soat
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <button onClick={() => setRateModal(null)} style={{
                flex: 1, background: 'transparent', border: '1px solid var(--border)',
                color: 'var(--text2)', padding: 10, borderRadius: 8, cursor: 'pointer', fontSize: 13
              }}>Bekor</button>
              <button onClick={saveRate} disabled={rateSaving} style={{
                flex: 1, background: rateInput.trim() ? 'linear-gradient(135deg, var(--cyan), #0369a1)' : 'var(--red)',
                border: 'none', color: 'white', padding: 10, borderRadius: 8,
                cursor: rateSaving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: rateSaving ? 0.7 : 1
              }}>
                {rateSaving ? 'Saqlanmoqda...' : rateInput.trim() ? 'Saqlash' : 'Narxni o\'chirish'}
              </button>
            </div>
            <button onClick={() => { setRateModal(null); openSpecsModal(rateModal); }} style={{
              width: '100%', background: 'transparent', border: '1px solid var(--border)',
              color: 'var(--cyan)', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12, marginBottom: 6
            }}>🔧 Texnik xarakteristikalar →</button>
            <button onClick={resetHardware} disabled={hwResetting} style={{
              width: '100%', background: 'transparent', border: '1px solid var(--red)',
              color: 'var(--red)', padding: '8px 10px', borderRadius: 8, cursor: hwResetting ? 'not-allowed' : 'pointer',
              fontSize: 12, opacity: hwResetting ? 0.6 : 1
            }}>
              {hwResetting ? 'Tozalanmoqda...' : '🔄 Hardware ID reset (hardware_mismatch xatosi bo\'lsa)'}
            </button>
          </div>
        </div>
      )}

      {/* Specs Modal */}
      {specsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 32, width: 400, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: 4 }}>🔧 PC-{String(specsModal.number).padStart(2, '0')} — Texnik xarakteristikalar</h3>
            <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
              Foydalanuvchilar bu ma'lumotlarni mobil ilovada ko'radi.
            </p>
            {[
              { key: 'cpu',          label: '💻 CPU',          placeholder: 'Intel Core i7-12700' },
              { key: 'gpu',          label: '🎮 GPU',          placeholder: 'RTX 3070 8GB' },
              { key: 'ram_gb',       label: '🧠 RAM (GB)',      placeholder: '16' },
              { key: 'monitor_hz',   label: '🖥 Monitor Hz',    placeholder: '144' },
              { key: 'monitor_inch', label: '🖥 Monitor inch',  placeholder: '27' },
              { key: 'peripherals',  label: '⌨️ Peripheral',   placeholder: 'Keyboard, Mouse, Headset' },
              { key: 'photo_url',    label: '📷 Rasm URL',      placeholder: 'https://...' },
            ].map(({ key, label, placeholder }) => (
              <div key={key} style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 5 }}>{label}</label>
                <input
                  type={key === 'ram_gb' || key === 'monitor_hz' || key === 'monitor_inch' ? 'number' : 'text'}
                  value={specsForm[key]}
                  onChange={e => setSpecsForm(f => ({ ...f, [key]: e.target.value }))}
                  placeholder={placeholder}
                  style={{ width: '100%', background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '9px 12px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              <button onClick={() => setSpecsModal(null)} style={{ flex: 1, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)', padding: 10, borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Bekor</button>
              <button onClick={saveSpecs} disabled={specsSaving} style={{ flex: 1, background: 'linear-gradient(135deg, var(--cyan), #0369a1)', border: 'none', color: 'white', padding: 10, borderRadius: 8, cursor: specsSaving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: specsSaving ? 0.7 : 1 }}>
                {specsSaving ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'var(--card)', border: '1px solid var(--border)',
            borderRadius: 16, padding: 32, width: 360
          }}>
            <h3 style={{ marginBottom: 20 }}>{t('addPcTitle')}</h3>
            <form onSubmit={addComputer}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>{t('branchLabel')}</label>
                <select value={form.lab_id} onChange={e => setForm({ ...form, lab_id: e.target.value })}
                  style={{ width: '100%', background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 14px', borderRadius: 8, fontSize: 13 }}>
                  {labs.map(lab => <option key={lab.id} value={lab.id}>{lab.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>QURILMA TURI</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 4 }}>
                  {Object.entries(TYPE_META).map(([key, meta]) => (
                    <button key={key} type="button" onClick={() => setForm({ ...form, type: key })} style={{
                      padding: '8px 4px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, textAlign: 'center',
                      background: form.type === key ? 'rgba(0,212,255,0.15)' : 'var(--card2)',
                      border: `1px solid ${form.type === key ? 'var(--cyan)' : 'var(--border)'}`,
                      color: form.type === key ? 'var(--cyan)' : 'var(--text2)',
                    }}>
                      <div style={{ fontSize: 18 }}>{meta.icon}</div>
                      <div style={{ fontSize: 10, marginTop: 2 }}>{meta.label}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>RAQAM</label>
                <input type="number" required value={form.number} onChange={e => setForm({ ...form, number: e.target.value })}
                  placeholder="1"
                  style={{ width: '100%', background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              {form.type !== 'pc' && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>SOATLIK NARX (so'm, ixtiyoriy)</label>
                  <input type="number" value={form.hourly_rate} onChange={e => setForm({ ...form, hourly_rate: e.target.value })}
                    placeholder="15000"
                    style={{ width: '100%', background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setShowAdd(false)} style={{
                  flex: 1, background: 'transparent', border: '1px solid var(--border)',
                  color: 'var(--text2)', padding: 10, borderRadius: 8, cursor: 'pointer', fontSize: 13
                }}>{t('cancelShort')}</button>
                <button type="submit" style={{
                  flex: 1, background: 'linear-gradient(135deg, var(--purple), #5b21b6)',
                  border: 'none', color: 'white', padding: 10, borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600
                }}>{t('add')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function BulkRateBox({ computers, selectedLab, onDone }) {
  const [bulkRate, setBulkRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const withRate = computers.filter(c => c.hourly_rate);
  const avgRate = withRate.length
    ? Math.round(withRate.reduce((s, c) => s + parseFloat(c.hourly_rate), 0) / withRate.length)
    : null;

  const applyAll = async () => {
    const rate = bulkRate.trim() === '' ? null : parseInt(bulkRate);
    if (rate !== null && (isNaN(rate) || rate < 0)) { setMsg('Noto\'g\'ri narx'); return; }
    setSaving(true);
    setMsg('');
    try {
      await Promise.all(computers.map(pc =>
        api.patch(`/owner/computers/${pc.id}`, { hourly_rate: rate })
      ));
      setMsg(`✓ ${computers.length} ta PC ga ${rate ? rate.toLocaleString() + ' so\'m/soat' : 'narx o\'chirildi'}`);
      onDone();
    } catch (e) {
      setMsg('Xato yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      background: 'var(--card2)', border: '1px solid var(--border)',
      borderRadius: 14, padding: 20, marginBottom: 20
    }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>⏱ Soatbay narx (paketsiz kirish)</div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
        Foydalanuvchi paket tanlamay kirganda soatbay hisoblanadi.
        {avgRate ? ` Hozirgi o'rtacha: ${avgRate.toLocaleString()} so'm/soat (${withRate.length}/${computers.length} PC).` : ` Hozircha hech bir PC da narx yo'q.`}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input
          type="number"
          min="0"
          step="500"
          value={bulkRate}
          onChange={e => { setBulkRate(e.target.value); setMsg(''); }}
          placeholder="15000"
          style={{
            width: 150, background: 'var(--card)', border: '1px solid var(--border)',
            color: 'var(--text)', padding: '8px 14px', borderRadius: 8, fontSize: 14, outline: 'none'
          }}
        />
        <span style={{ fontSize: 13, color: 'var(--text2)' }}>so'm/soat</span>
        <button onClick={applyAll} disabled={saving} style={{
          background: bulkRate.trim() ? 'linear-gradient(135deg, var(--cyan), #0369a1)' : 'var(--red)',
          border: 'none', color: 'white', padding: '8px 20px', borderRadius: 8,
          fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1
        }}>
          {saving ? 'Saqlanmoqda...' : bulkRate.trim() ? `Hammaga qo'y` : 'Hammadan o\'chir'}
        </button>
        {msg && <span style={{ fontSize: 12, color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{msg}</span>}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 10 }}>
        Yoki har bir PC ni alohida sozlash uchun yuqoridagi kartalarga bosing.
      </div>
    </div>
  );
}
